// Copyright 2026 Epic Digital Interactive Media LLC
// SPDX-License-Identifier: Apache-2.0

/**
 * Agent Lookup Builder
 *
 * Builds the identifier → {teamMemberId, userId, isAgent} map used by
 * `prepareDiscussionDoc` to resolve `@handle` mentions at write time.
 *
 * Pure function: takes already-fetched teammember and user rows and returns
 * the lookup Map. The d1-worker layer queries Drizzle directly and passes
 * the arrays in — keeps `db-collections` free of D1/Drizzle dependencies.
 */

import type { AgentLookupEntry } from './mentionResolver'

/**
 * Minimal teammember shape needed for lookup-building.
 * Matches the columns the d1-worker queries.
 */
export interface TeamMemberLookupRow {
  id: string
  slug?: string | null
  userName?: string | null
  isAgent?: boolean | null
}

/**
 * Minimal user shape needed for lookup-building.
 * Email handle (text before `@`) becomes the identifier key.
 */
export interface UserLookupRow {
  id: string
  email?: string | null
}

export interface BuildAgentLookupInput {
  teammembers: ReadonlyArray<TeamMemberLookupRow>
  users: ReadonlyArray<UserLookupRow>
}

/**
 * Convert a userName-style display name into a kebab-cased lookup key.
 * Example: "Marcus Chen" → "marcus-chen".
 *
 * Strips any character that isn't a letter, digit, hyphen, or underscore;
 * collapses runs of separators into a single hyphen; lowercases the result.
 */
function kebabize(input: string): string {
  return input
    .toLowerCase()
    .replace(/[^a-z0-9_-]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-+|-+$/g, '')
}

/**
 * Extract the local-part of an email address for use as a mention handle.
 * Example: "spencer@thornock.net" → "spencer".
 */
function emailHandle(email: string): string {
  const at = email.indexOf('@')
  return at === -1 ? email.toLowerCase() : email.slice(0, at).toLowerCase()
}

/**
 * Build the identifier → AgentLookupEntry map.
 *
 * Each teammember contributes:
 * - Primary key: `slug` (post-PR-272 canonical identifier)
 * - Fallback key: kebab-cased `userName` (legacy rows where operators sometimes
 *   type `@marcus-chen` from the human-readable display name)
 *
 * Each user contributes:
 * - Email-handle key (e.g. `spencer@thornock.net` → `spencer`)
 *
 * Lookup is case-insensitive: keys are lowercased on insert; the parser
 * already lowercases extracted identifiers.
 *
 * Conflict policy: teammember entries are written first. A user's email
 * handle that collides with a teammember slug WILL NOT overwrite the
 * teammember entry — teammember wins.
 */
export function buildAgentLookup(input: BuildAgentLookupInput): Map<string, AgentLookupEntry> {
  const lookup = new Map<string, AgentLookupEntry>()

  for (const tm of input.teammembers) {
    if (!tm.id) continue
    const isAgent = !!tm.isAgent

    if (tm.slug) {
      const key = tm.slug.toLowerCase()
      if (!lookup.has(key)) {
        lookup.set(key, { teamMemberId: tm.id, isAgent })
      }
    }

    if (tm.userName) {
      const key = kebabize(tm.userName)
      if (key && !lookup.has(key)) {
        lookup.set(key, { teamMemberId: tm.id, isAgent })
      }
    }
  }

  for (const u of input.users) {
    if (!u.id || !u.email) continue
    const key = emailHandle(u.email)
    if (!key || lookup.has(key)) continue
    // User-only entry: no teammember linkage, but we know the userId.
    lookup.set(key, { teamMemberId: '', userId: u.id, isAgent: false })
  }

  return lookup
}
